/** Session key for Telr payment status written by return pages. */
export const TELR_PAYMENT_STATUS_KEY = 'telrPaymentStatus';

/** Session key for the app route to reopen after Telr returns. */
export const TELR_PAYMENT_RETURN_URL_KEY = 'telrPaymentReturnUrl';

/** sessionStorage flag set while a Telr checkout popup is open. */
export const TELR_CHECKOUT_POPUP_FLAG_KEY = 'telrCheckoutPopup';

/** `window.name` used by `window.open(paymentLink, 'popup', ...)`. */
export const TELR_CHECKOUT_POPUP_WINDOW_NAME = 'popup';

/** postMessage `source` used by Telr return pages to notify the opener. */
export const TELR_PAYMENT_MESSAGE_SOURCE = 'carhealquote-telr';

/**
 * Telr hosted-payment return statuses (route/query/sessionStorage values).
 */
export enum TelrPaymentStatus {
  Authorised = 'authorised',
  Declined = 'declined',
  Cancelled = 'cancelled'
}

/** Default plans page when no return URL was stored before checkout. */
export const TELR_DEFAULT_PLANS_PATH = '/ai-studio/subscriptions';

/**
 * Normalizes a Telr/query status string to a known payment status.
 * @param status Raw status from route data, query params, or session storage.
 * @returns Normalized status, or null when unrecognized.
 */
export function normalizeTelrPaymentStatus(status: string | null | undefined): TelrPaymentStatus | null {
  const normalized = (status ?? '').trim().toLowerCase();
  if (normalized === TelrPaymentStatus.Authorised || normalized === 'authorized') {
    return TelrPaymentStatus.Authorised;
  }
  if (normalized === TelrPaymentStatus.Declined) {
    return TelrPaymentStatus.Declined;
  }
  if (normalized === TelrPaymentStatus.Cancelled || normalized === 'canceled') {
    return TelrPaymentStatus.Cancelled;
  }
  return null;
}

/**
 * Stores the in-app route to restore after Telr redirects back.
 * Uses `sessionStorage` (same-tab checkout; cleared when the tab closes).
 * Query/hash are stripped so only the path is restored.
 * @param returnUrl Absolute app path or full router URL (e.g. `/settings/subscriptions/plan/81`).
 */
export function storeTelrPaymentReturnUrl(returnUrl: string): void {
  const path = (returnUrl ?? '').split('?')[0].split('#')[0].trim();
  if (!path || path === '/') {
    return;
  }
  sessionStorage.setItem(TELR_PAYMENT_RETURN_URL_KEY, path);
}

/**
 * Marks that Telr checkout was opened in a named popup window.
 */
export function markTelrCheckoutPopupOpen(): void {
  sessionStorage.setItem(TELR_CHECKOUT_POPUP_FLAG_KEY, '1');
}

/**
 * Clears the Telr checkout popup marker.
 */
export function clearTelrCheckoutPopupFlag(): void {
  sessionStorage.removeItem(TELR_CHECKOUT_POPUP_FLAG_KEY);
}

/**
 * Detects whether the current window is the Telr payment popup.
 * `window.opener` is often cleared after Telr's cross-origin redirect, so we also
 * check the popup window name and a sessionStorage flag set before checkout.
 * @returns true when this window should stay on the lightweight return page.
 */
export function isTelrCheckoutPopupWindow(): boolean {
  if (window.opener && !window.opener.closed) {
    return true;
  }
  if (window.name === TELR_CHECKOUT_POPUP_WINDOW_NAME) {
    return true;
  }
  return sessionStorage.getItem(TELR_CHECKOUT_POPUP_FLAG_KEY) === '1';
}

/**
 * Reads and clears the stored Telr return path.
 * @returns Stored path, or the default AI Studio plans path.
 */
export function consumeTelrPaymentReturnUrl(): string {
  const fromSession = sessionStorage.getItem(TELR_PAYMENT_RETURN_URL_KEY);
  if (fromSession) {
    sessionStorage.removeItem(TELR_PAYMENT_RETURN_URL_KEY);
    return fromSession;
  }

  return TELR_DEFAULT_PLANS_PATH;
}

/**
 * Persists Telr payment status for the return hand-off.
 * Uses `sessionStorage` for same-tab checkout.
 * @param status Authorised, declined, or cancelled.
 */
export function storeTelrPaymentStatus(status: TelrPaymentStatus): void {
  sessionStorage.setItem(TELR_PAYMENT_STATUS_KEY, status);
}

/**
 * Reads a stored Telr payment status without clearing it.
 * @returns Normalized status, or null when missing/invalid.
 */
export function peekTelrPaymentStatus(): TelrPaymentStatus | null {
  return normalizeTelrPaymentStatus(sessionStorage.getItem(TELR_PAYMENT_STATUS_KEY));
}

/**
 * Reads and clears a stored Telr payment status.
 * @returns Normalized status, or null when missing/invalid.
 */
export function consumeTelrPaymentStatus(): TelrPaymentStatus | null {
  const stored = sessionStorage.getItem(TELR_PAYMENT_STATUS_KEY);
  if (!stored) {
    return null;
  }
  sessionStorage.removeItem(TELR_PAYMENT_STATUS_KEY);
  return normalizeTelrPaymentStatus(stored);
}
